import { feature, featureCollection, geometry } from '@turf/turf';
import dayjs from 'dayjs';
import mapboxgl from 'mapbox-gl';
import { OrderInstruction } from '../../../components/maps/BundleMap';
import { DirectionsData } from '../../../fetch/mapboxApi';
import { CourierJson } from '../../../types/bundleTypes';
import { flatten } from '../../../utils/array';
import { renderInstructionPopup, renderCourierPopup } from './bundleMapPopup';
import {
  WAYPOINT_SOURCE,
  WAYPOINT_LINE_LAYER,
  ORDER_INSTRUCTIONS_SOURCE,
  ORDER_INSTRUCTIONS_LAYER,
  ORDER_INSTRUCTIONS_LABEL_LAYER,
  COURIER_SOURCE,
  COURIER_SOURCE_CIRCLE_LAYER,
  COURIER_SOURCE_LABEL_LAYER,
} from './BundleMapboxMap';

export function addWaypointLayer(
  map: mapboxgl.Map,
  directions: DirectionsData
) {
  map
    .addSource(WAYPOINT_SOURCE, {
      type: 'geojson',
      data: feature(geometry('LineString', directions.routeCoordinates)),
    })
    .addLayer({
      id: WAYPOINT_LINE_LAYER,
      source: WAYPOINT_SOURCE,
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': 'rgba(102, 26, 255, 0.6)',
        'line-width': 5,
      },
    });
}
export function addInstructionsMarkers(
  map: mapboxgl.Map,
  deliveryInstructions: OrderInstruction[],
  windows: [string, string][],
  popup: mapboxgl.Popup,
  shouldFitToBounds: boolean
) {
  const dataSource = featureCollection(
    deliveryInstructions.map(instruction =>
      feature(
        geometry('Point', [
          instruction.order.destinationAddress.location.longitude,
          instruction.order.destinationAddress.location.latitude,
        ]),
        {
          type: 'Delivery',
          deviated:
            instruction.order.openCourierDeviation ||
            instruction.order.deviations?.length > 0,
          orderId: instruction.order.id,
          label: instruction.label,
          idWithListNumber: instruction.order.idWithListNumber,
          address: instruction.order.recipientAddress,
          name: instruction.order.pickupName,
          phone: instruction.order.pickupPhone,
          window: `${dayjs(instruction.order.deliverFrom).format(
            'HH:mm'
          )} - ${dayjs(instruction.order.deliverUntil).format('HH:mm')}`,
        }
      )
    )
  );

  const coordinates = dataSource.features.map(
    feature => feature.geometry.type === 'Point' && feature.geometry.coordinates
  ) as [number, number][];

  const bounds = coordinates.reduce((bounds, coord) => {
    return bounds.extend(coord);
  }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

  if (shouldFitToBounds && Object.keys(bounds).length) {
    map.fitBounds(bounds, {
      padding: 50,
    });
  }

  map.addSource(ORDER_INSTRUCTIONS_SOURCE, {
    type: 'geojson',
    data: dataSource,
  });

  map
    .addLayer({
      id: ORDER_INSTRUCTIONS_LAYER,
      source: ORDER_INSTRUCTIONS_SOURCE,
      type: 'circle',
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['get', 'deviated']],
          'yellow',
          'white',
        ],
        'circle-radius': 12,
        'circle-stroke-color': [
          'match',
          ['get', 'window'],
          ...flatten(windows).map(w => w),
          '#661aff',
        ],
        'circle-stroke-width': 4,
      },
    })
    .addLayer({
      id: ORDER_INSTRUCTIONS_LABEL_LAYER,
      type: 'symbol',
      source: ORDER_INSTRUCTIONS_SOURCE,
      layout: {
        'text-field': '{label}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    });

  map.on('click', ORDER_INSTRUCTIONS_LAYER, e => {
    renderInstructionPopup(e, popup, map);
  });
}
export function addCourierMarker(
  map: mapboxgl.Map,
  courier: CourierJson,
  popup: mapboxgl.Popup
) {
  // We might want to add a custom icon for the courier location
  map
    .addSource(COURIER_SOURCE, {
      type: 'geojson',
      data: feature(geometry('Point', [courier.longitude, courier.latitude]), {
        type: 'courier',
        courierName: `${courier.firstName}${
          courier.lastName ? ` ${courier.lastName}` : ''
        }`,
        locationUpdatedAt: courier.locationUpdatedAt,
      }),
    })
    .addLayer({
      id: COURIER_SOURCE_CIRCLE_LAYER,
      source: COURIER_SOURCE,
      type: 'circle',
      paint: {
        'circle-color': 'white',
        'circle-radius': 12,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#661aff',
      },
    })
    .addLayer({
      id: COURIER_SOURCE_LABEL_LAYER,
      type: 'symbol',
      source: COURIER_SOURCE,
      layout: {
        'text-field': 'C',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    });

  map.on('click', COURIER_SOURCE_CIRCLE_LAYER, e => {
    renderCourierPopup(e, popup, map);
  });
}
export function cleanupLayersAndSources(map: mapboxgl.Map) {
  map.getLayer(ORDER_INSTRUCTIONS_LAYER) &&
    map.removeLayer(ORDER_INSTRUCTIONS_LAYER);
  map.getLayer(ORDER_INSTRUCTIONS_LABEL_LAYER) &&
    map.removeLayer(ORDER_INSTRUCTIONS_LABEL_LAYER);
  map.getSource(ORDER_INSTRUCTIONS_SOURCE) &&
    map.removeSource(ORDER_INSTRUCTIONS_SOURCE);
  map.getLayer(COURIER_SOURCE_CIRCLE_LAYER) &&
    map.removeLayer(COURIER_SOURCE_CIRCLE_LAYER);
  map.getLayer(COURIER_SOURCE_LABEL_LAYER) &&
    map.removeLayer(COURIER_SOURCE_LABEL_LAYER);
  map.getSource(COURIER_SOURCE) && map.removeSource(COURIER_SOURCE);
  map.getLayer(WAYPOINT_LINE_LAYER) && map.removeLayer(WAYPOINT_LINE_LAYER);
  map.getSource(WAYPOINT_SOURCE) && map.removeSource(WAYPOINT_SOURCE);
}
